// ЗАЯВКА ЧЕРЕЗ WHATSAPP.
//
// Все целевые кнопки сайта («Забронировать», «Оставить заявку», «Подобрать
// поездку») ведут в WhatsApp с уже написанным сообщением. Смысл в том, что
// человеку не нужно ничего печатать и ничего объяснять менеджеру: в чате
// сразу видно, какой тур, на какие даты, за сколько и какие опции выбраны,
// плюс ссылка на страницу, с которой человек пришёл.
//
// Здесь только сборка текста и ссылки. Кто и как рисует кнопку —
// src/sections/components/whatsapp-cta.jsx.
import { CONTACTS } from "src/config/contacts";
import { formatDateRange } from "./dates";
import { pick } from "./localize";

// Текст уходит в адресной строке. wa.me принимает длинные ссылки, но на
// части телефонов хвост обрезается молча — лучше обрезать самим и явно.
const MAX_LENGTH = 1500;

export const hasWhatsapp = () => Boolean(CONTACTS.whatsapp);

/**
 * Собирает сообщение из частей:
 *   intro   — первая фраза («Здравствуйте! Хочу забронировать …»)
 *   details — пары [подпись, значение]; пустые значения выпадают, чтобы в
 *             чат не уходило «Дата:» без даты
 *   extra   — готовый блок текста (например, выбранные опции калькулятора)
 *   url     — адрес страницы: менеджер открывает ровно то, что видел человек
 */
export const buildWhatsappMessage = ({ intro = "", details = [], extra = "", url = "" }) => {
  const rows = details
    .filter((row) => Array.isArray(row) && row[1])
    .map(([label, value]) => `${label}: ${value}`);

  const message = [intro, rows.join("\n"), extra, url].filter(Boolean).join("\n\n");
  return message.length > MAX_LENGTH
    ? `${message.slice(0, MAX_LENGTH - 1).trimEnd()}…`
    : message;
};

// Номер не заполнен — возвращаем пустую строку. Вызывающий код в этом случае
// показывает форму заявки: кнопка, ведущая в никуда, хуже формы.
export const whatsappHref = (message) => {
  if (!CONTACTS.whatsapp) return "";
  const [base] = CONTACTS.whatsapp.split("?");
  return `${base}?text=${encodeURIComponent(message)}`;
};

export const pageUrl = () =>
  typeof window === "undefined" ? "" : window.location.href;

// Ссылка без конкретного тура: «Здравствуйте, хочу поехать в Японию» плюс
// адрес страницы, с которой человек написал. Для кнопок в футере, FAQ и на
// странице контактов — там предмет разговора заранее неизвестен.
export const genericWhatsappHref = (t) =>
  whatsappHref(
    buildWhatsappMessage({ intro: t("request.whatsappTextGeneric"), url: pageUrl() })
  );

// Всё, что известно о туре, — в порядке, в котором это читает менеджер.
//
// ЦЕНЫ В СООБЩЕНИИ НЕТ НАМЕРЕННО. Цифра со страницы — ориентир: она не знает
// ни про размер группы, ни про даты, ни про выбранные опции. Подставленная в
// первое же сообщение, она читается как согласованная сумма, и разговор
// начинается со спора о цене вместо разговора о поездке. Взнос за место
// (`deposit`) остаётся: это фиксированная сумма брони, а не стоимость тура.
export const tourDetails = (activity, { t, lang }) => {
  if (!activity) return [];
  const route = (activity.route || [])
    .map((stop) => pick(stop, lang))
    .filter(Boolean)
    .join(" → ");

  return [
    [t("actInfo.date"), formatDateRange(activity.date, activity.dateEnd, lang)],
    [t("tour.routeTitle"), route],
    [t("tour.depositTitle"), activity.deposit],
  ];
};

// У услуги из карточки брать нечего, кроме названия: цена туда не идёт по той
// же причине, что и у тура. Название подставляется в первую фразу.
export const serviceDetails = () => [];

// Ответы анкеты — тем же форматом «подпись: значение», что и данные тура,
// чтобы в чате всё сообщение читалось одним списком.
export const answerDetails = (answers, fields, t) =>
  fields
    .map((field) => [t(field.msgLabel || field.label), formatAnswer(answers[field.key], field, t)])
    .filter(([, value]) => value);

const formatAnswer = (value, field, t) => {
  if (value === undefined || value === null || value === "") return "";
  // Значение из выпадающего списка приходит кодом («large»), а в чат должно
  // уйти словом — на языке, на котором человек смотрит сайт.
  if (field.options) {
    const option = field.options.find((o) => o.value === value);
    return option ? t(option.label) : String(value);
  }
  return String(value).trim();
};
